/**
 * ShiftImportForm - Tabbed Import Form for Email Parser Workflow
 * 
 * EF-INBOX-SHIFT-IMPORT-WORKFLOW-V1: Initial implementation
 * EF-SHIFT-IMPORT-RULES-DUPLICATION-FIX-V1: Rules engine integration
 * EF-SHIFT-IMPORT-UI-RESTORE-TABBED-V1: Tabbed layout with all fields restored
 * 
 * Provides a tabbed interface for reviewing and editing parsed email data
 * before creating project and shift.
 */
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Loader2,
  Calendar,
  Clock,
  MapPin,
  FolderKanban,
  Users,
  AlertCircle,
  Timer,
  CheckCircle,
  Info,
  AlertTriangle,
  Building,
  FileText,
  Briefcase,
  Sparkles,
  Copy,
  ArrowRight,
  ClipboardList,
  MessageSquare,
  Settings2,
  Plus,
  Trash2,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";
import api, { getClients, getVenues, getVenueLocations } from "../../lib/api";
import { ShiftDuplicationModal } from "../shifts/ShiftDuplicationModal";

/**
 * Confidence badge component
 */
const ConfidenceBadge = ({ level }) => {
  const styles = {
    HIGH: "bg-green-500/20 text-green-400 border-green-500/30",
    MEDIUM: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    LOW: "bg-red-500/20 text-red-400 border-red-500/30",
  };
  
  return (
    <Badge variant="outline" className={cn("text-xs ml-2", styles[level] || styles.LOW)}>
      {level}
    </Badge>
  );
};

/**
 * Warning badge for unmatched fields
 */
const UnmatchedWarning = ({ message }) => (
  <div className="flex items-center gap-1 text-xs text-amber-500 mt-1">
    <AlertTriangle className="w-3 h-3" />
    {message}
  </div>
);

export function ShiftImportForm({
  isOpen,
  onClose,
  parsedData,
  messageId,
  onSuccess,
}) {
  const navigate = useNavigate();
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Current tab
  const [activeTab, setActiveTab] = useState("project");
  
  // Dropdown data
  const [clients, setClients] = useState([]);
  const [venues, setVenues] = useState([]);
  const [venueLocations, setVenueLocations] = useState([]);
  const [taskDefinitions, setTaskDefinitions] = useState([]);
  
  // Form state - Project section
  const [projectData, setProjectData] = useState({
    client_id: "",
    client_name: "",
    project_name: "",
    start_date: "",
    end_date: "",
  });
  
  // Form state - Shift section
  const [shiftData, setShiftData] = useState({
    name: "",
    venue_id: "",
    venue_name: "",
    venue_location_id: "",
    venue_location_name: "",
    date: "",
    start_time: "09:00",
    end_time: "17:00",
    duration_hours: 8,
    staff_required: 1,
    notes: "",
  });
  
  // Form state - Tasks
  const [taskItems, setTaskItems] = useState([]);
  
  // Form state - Crew Requirements
  const [crewRequirements, setCrewRequirements] = useState([
    { role_id: "", role_name: "General Crew", quantity: 1 }
  ]);
  
  // Form state - Supplements (rule-managed, informational only)
  const [ruleSupplements, setRuleSupplements] = useState([]);
  
  // Validation errors
  const [errors, setErrors] = useState({});
  
  // Duplication modal state
  const [showDuplicationModal, setShowDuplicationModal] = useState(false);
  const [createdProjectId, setCreatedProjectId] = useState(null);
  const [createdShiftId, setCreatedShiftId] = useState(null);
  
  // Calculate project day count
  const projectDayCount = useMemo(() => {
    if (!projectData.start_date || !projectData.end_date) return 1;
    const start = new Date(projectData.start_date);
    const end = new Date(projectData.end_date);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    return days > 0 ? days : 1;
  }, [projectData.start_date, projectData.end_date]);
  
  // Check if project spans multiple days
  const isMultiDayProject = projectDayCount > 1;
  
  // Calculate totals
  const totalCrew = shiftData.staff_required * projectDayCount;
  const totalHours = shiftData.duration_hours * projectDayCount;
  
  // Load dropdown data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientsRes, venuesRes, locationsRes, tasksRes] = await Promise.all([
          getClients({ limit: 100 }),
          getVenues({ limit: 100 }),
          getVenueLocations({ limit: 500 }),
          api.get('/task-definitions', { params: { limit: 100, enabled_filter: "enabled" } }),
        ]);
        setClients(clientsRes.data || []);
        setVenues(venuesRes.data || []);
        setVenueLocations(locationsRes.data || []);
        setTaskDefinitions(tasksRes.data || []);
      } catch (err) {
        console.error("Failed to load data:", err);
        toast.error("Failed to load form data");
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen) {
      setLoading(true);
      loadData();
    }
  }, [isOpen]);
  
  // Prefill form from parsed data
  useEffect(() => {
    if (isOpen && parsedData && !loading) {
      // Prefill project data
      setProjectData({
        client_id: parsedData.client_id || "",
        client_name: parsedData.client_name || "",
        project_name: parsedData.project_name || "",
        start_date: parsedData.start_date || "",
        end_date: parsedData.end_date || "",
      });
      
      // Prefill shift data from first shift
      const firstShift = parsedData.shifts?.[0] || {};
      setShiftData({
        name: firstShift.name || parsedData.project_name || "",
        venue_id: firstShift.venue_id || "",
        venue_name: firstShift.venue_name || "",
        venue_location_id: firstShift.venue_location_id || "",
        venue_location_name: firstShift.venue_location_name || "",
        date: firstShift.date || parsedData.start_date || "",
        start_time: firstShift.start_time || "09:00",
        end_time: firstShift.end_time || "17:00",
        duration_hours: firstShift.duration_hours || 8,
        staff_required: firstShift.staff_required || 1,
        notes: firstShift.notes || "",
      });
      
      // Prefill tasks
      const tasks = firstShift.task_items || [];
      setTaskItems(tasks.map((t, i) => ({
        id: `task-${i}`,
        name: typeof t === 'string' ? t : t.name || '',
        quantity: typeof t === 'object' ? t.quantity || 1 : 1,
        role_id: typeof t === 'object' ? t.role_id || '' : '',
      })));
      
      // Set crew requirements from staff_required
      setCrewRequirements([
        { role_id: "", role_name: "General Crew", quantity: firstShift.staff_required || 1 }
      ]);
      
      // Set rule-managed supplements
      setRuleSupplements(parsedData.rule_managed_supplements_detected || []);
      
      // Try to match client by name if ID not found
      if (!parsedData.client_id && parsedData.client_name) {
        const matchedClient = clients.find(c => 
          c.name.toLowerCase() === parsedData.client_name.toLowerCase()
        );
        if (matchedClient) {
          setProjectData(prev => ({ ...prev, client_id: matchedClient.id }));
        }
      }
      
      // Try to match venue by name
      if (!firstShift.venue_id && firstShift.venue_name) {
        const matchedVenue = venues.find(v =>
          v.name.toLowerCase().includes(firstShift.venue_name.toLowerCase()) ||
          firstShift.venue_name.toLowerCase().includes(v.name.toLowerCase())
        );
        if (matchedVenue) {
          setShiftData(prev => ({ ...prev, venue_id: matchedVenue.id }));
        }
      }
    }
  }, [isOpen, parsedData, loading, clients, venues]);
  
  // Filter venue locations by selected venue
  const filteredLocations = useMemo(() => {
    if (!shiftData.venue_id) return [];
    return venueLocations.filter(vl => vl.venue_id === shiftData.venue_id);
  }, [shiftData.venue_id, venueLocations]);
  
  // Handle venue change - also reset location
  const handleVenueChange = (venueId) => {
    const venue = venues.find(v => v.id === venueId);
    setShiftData(prev => ({
      ...prev,
      venue_id: venueId,
      venue_name: venue?.name || "",
      venue_location_id: "",
      venue_location_name: "",
    }));
  };
  
  // Handle location change
  const handleLocationChange = (locationId) => {
    const location = venueLocations.find(l => l.id === locationId);
    setShiftData(prev => ({
      ...prev,
      venue_location_id: locationId,
      venue_location_name: location?.name || "",
    }));
  };
  
  // Add task
  const addTask = () => {
    setTaskItems(prev => [...prev, {
      id: `task-${Date.now()}`,
      name: "",
      quantity: 1,
      role_id: "",
    }]);
  };
  
  // Remove task
  const removeTask = (taskId) => {
    setTaskItems(prev => prev.filter(t => t.id !== taskId));
  };
  
  // Update task
  const updateTask = (taskId, field, value) => {
    setTaskItems(prev => prev.map(t => 
      t.id === taskId ? { ...t, [field]: value } : t
    ));
  };
  
  // Update crew requirement
  const updateCrewRequirement = (index, field, value) => {
    setCrewRequirements(prev => prev.map((r, i) => 
      i === index ? { ...r, [field]: value } : r
    ));
    // Also update shift staff_required
    if (field === 'quantity' && index === 0) {
      setShiftData(prev => ({ ...prev, staff_required: value }));
    }
  };
  
  // Calculate estimated rule supplements
  const estimatedRuleSupplements = useMemo(() => {
    const supplements = [];
    if (shiftData.staff_required >= 4) {
      supplements.push("Crew Chief Supplement");
    }
    if (shiftData.duration_hours > 5) {
      supplements.push("Meal Allowance");
    }
    if (shiftData.start_time && parseInt(shiftData.start_time.split(':')[0]) >= 22) {
      supplements.push("Night Allowance");
    }
    return supplements;
  }, [shiftData.staff_required, shiftData.duration_hours, shiftData.start_time]);
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!projectData.client_id) newErrors.client_id = "Client is required";
    if (!projectData.project_name) newErrors.project_name = "Project name is required";
    if (!projectData.start_date) newErrors.start_date = "Start date is required";
    if (!projectData.end_date) newErrors.end_date = "End date is required";
    
    if (!shiftData.name) newErrors.shift_name = "Shift name is required";
    // EF-SHIFT-IMPORT-LOCATION-VALIDATION-HARD-STOP-V1: Validate both venue and location
    if (!shiftData.venue_id) newErrors.venue_id = "Venue is required";
    if (!shiftData.venue_location_id) newErrors.venue_location_id = "A location must be selected for the venue";
    if (!shiftData.date) newErrors.shift_date = "Shift date is required";
    if (!shiftData.start_time) newErrors.start_time = "Start time is required";
    if (shiftData.staff_required < 1) newErrors.staff_required = "At least 1 crew required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle save shift
  const handleSaveShift = async () => {
    // EF-SHIFT-IMPORT-LOCATION-VALIDATION-HARD-STOP-V1: Early validation with friendly message
    if (!shiftData.venue_id || !shiftData.venue_location_id) {
      toast.error("Venue and location must be selected before saving the shift.");
      setActiveTab("shift");
      return;
    }
    
    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      // Navigate to the tab with errors
      if (errors.client_id || errors.project_name || errors.start_date || errors.end_date) {
        setActiveTab("project");
      } else if (errors.shift_name || errors.venue_id || errors.venue_location_id || errors.shift_date || errors.start_time) {
        setActiveTab("shift");
      }
      return;
    }
    
    setSaving(true);
    
    try {
      const response = await api.post(`/inbox/messages/${messageId}/import-shift`, {
        project: {
          client_id: projectData.client_id,
          name: projectData.project_name,
          start_date: projectData.start_date,
          end_date: projectData.end_date,
        },
        shift: {
          name: shiftData.name,
          venue_id: shiftData.venue_id,
          venue_location_id: shiftData.venue_location_id,
          date: shiftData.date,
          start_time: shiftData.start_time,
          end_time: shiftData.end_time,
          duration_hours: shiftData.duration_hours,
          staff_required: shiftData.staff_required,
          task_items: taskItems.filter(t => t.name).map(t => t.name),
          notes: shiftData.notes,
        },
      });
      
      if (response.data.success) {
        const { project_id, shift_id, shifts_created, rules_applied } = response.data;
        setCreatedProjectId(project_id);
        setCreatedShiftId(shift_id);
        
        const rulesMsg = rules_applied?.length > 0 
          ? ` (${rules_applied.length} rules applied)` 
          : '';
        toast.success(`Created project with ${shifts_created} shift${rulesMsg}`);
        
        if (isMultiDayProject) {
          setShowDuplicationModal(true);
        } else {
          onSuccess?.(response.data);
          onClose();
          navigate(`/projects/${project_id}`);
        }
      } else {
        toast.error(response.data.message || "Failed to create project");
      }
    } catch (err) {
      console.error("Import failed:", err);
      toast.error(err.response?.data?.message || "Failed to import shift");
    } finally {
      setSaving(false);
    }
  };
  
  // Handle duplication confirm
  const handleDuplicationConfirm = async (selectedDates) => {
    if (!createdProjectId || !createdShiftId) return;
    
    try {
      const response = await api.post(`/shifts/${createdShiftId}/duplicate-multi`, {
        dates: selectedDates,
      });
      
      const result = response.data;
      const created = result.created || selectedDates.length;
      
      toast.success(`Duplicated shift to ${created} additional days`);
      
      setShowDuplicationModal(false);
      onSuccess?.({ project_id: createdProjectId, shifts_created: created + 1 });
      onClose();
      navigate(`/projects/${createdProjectId}`);
    } catch (err) {
      console.error("Duplication failed:", err);
      toast.error(err.response?.data?.detail || "Failed to duplicate shifts");
      setShowDuplicationModal(false);
      onSuccess?.({ project_id: createdProjectId });
      onClose();
      navigate(`/projects/${createdProjectId}`);
    }
  };
  
  // Handle single shift
  const handleSingleShift = () => {
    setShowDuplicationModal(false);
    onSuccess?.({ project_id: createdProjectId });
    onClose();
    navigate(`/projects/${createdProjectId}`);
  };
  
  // Handle cancel
  const handleCancel = () => {
    onClose();
  };
  
  return (
    <>
      <Dialog open={isOpen && !showDuplicationModal} onOpenChange={(open) => !open && handleCancel()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Import Shift from Email
            </DialogTitle>
            <DialogDescription>
              Review and adjust the parsed data before creating the project and shift.
            </DialogDescription>
          </DialogHeader>
          
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="flex flex-1 overflow-hidden">
              {/* Main Content - Tabs */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
                  <TabsList className="mx-6 mt-4 grid grid-cols-5 bg-surface-elevated">
                    <TabsTrigger value="project" className="flex items-center gap-1.5">
                      <FolderKanban className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Project</span>
                    </TabsTrigger>
                    <TabsTrigger value="shift" className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Shift</span>
                    </TabsTrigger>
                    <TabsTrigger value="tasks" className="flex items-center gap-1.5">
                      <ClipboardList className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Tasks</span>
                      {taskItems.length > 0 && (
                        <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                          {taskItems.length}
                        </Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="crew" className="flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Crew</span>
                    </TabsTrigger>
                    <TabsTrigger value="notes" className="flex items-center gap-1.5">
                      <MessageSquare className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Notes</span>
                    </TabsTrigger>
                  </TabsList>
                  
                  <div className="flex-1 overflow-hidden">
                    {/* TAB 1: Project */}
                    <TabsContent value="project" className="h-full m-0 p-0">
                      <ScrollArea className="h-full">
                        <div className="p-6 space-y-6">
                          <div className="grid grid-cols-2 gap-4">
                            {/* Client */}
                            <div className="space-y-2">
                              <Label className="flex items-center gap-1">
                                <Building className="h-3.5 w-3.5" />
                                Client *
                                {parsedData?.confidence?.client && (
                                  <ConfidenceBadge level={parsedData.confidence.client} />
                                )}
                              </Label>
                              <Select
                                value={projectData.client_id}
                                onValueChange={(v) => setProjectData(prev => ({ ...prev, client_id: v }))}
                              >
                                <SelectTrigger className={cn(errors.client_id && "border-destructive")}>
                                  <SelectValue placeholder="Select client" />
                                </SelectTrigger>
                                <SelectContent>
                                  {clients.map((client) => (
                                    <SelectItem key={client.id} value={client.id}>
                                      {client.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {!projectData.client_id && parsedData?.client_name && (
                                <UnmatchedWarning message={`No match for: ${parsedData.client_name}`} />
                              )}
                              {errors.client_id && <p className="text-xs text-destructive">{errors.client_id}</p>}
                            </div>
                            
                            {/* Project Name */}
                            <div className="space-y-2">
                              <Label className="flex items-center gap-1">
                                <FileText className="h-3.5 w-3.5" />
                                Project Name *
                                {parsedData?.confidence?.project && (
                                  <ConfidenceBadge level={parsedData.confidence.project} />
                                )}
                              </Label>
                              <Input
                                value={projectData.project_name}
                                onChange={(e) => setProjectData(prev => ({ ...prev, project_name: e.target.value }))}
                                placeholder="Enter project name"
                                className={cn(errors.project_name && "border-destructive")}
                              />
                              {errors.project_name && <p className="text-xs text-destructive">{errors.project_name}</p>}
                            </div>
                            
                            {/* Start Date */}
                            <div className="space-y-2">
                              <Label className="flex items-center gap-1">
                                <Calendar className="h-3.5 w-3.5" />
                                Project Start *
                              </Label>
                              <Input
                                type="date"
                                value={projectData.start_date}
                                onChange={(e) => setProjectData(prev => ({ ...prev, start_date: e.target.value }))}
                                className={cn(errors.start_date && "border-destructive")}
                              />
                              {errors.start_date && <p className="text-xs text-destructive">{errors.start_date}</p>}
                            </div>
                            
                            {/* End Date */}
                            <div className="space-y-2">
                              <Label className="flex items-center gap-1">
                                <Calendar className="h-3.5 w-3.5" />
                                Project End *
                              </Label>
                              <Input
                                type="date"
                                value={projectData.end_date}
                                onChange={(e) => setProjectData(prev => ({ ...prev, end_date: e.target.value }))}
                                className={cn(errors.end_date && "border-destructive")}
                              />
                              {errors.end_date && <p className="text-xs text-destructive">{errors.end_date}</p>}
                            </div>
                          </div>
                          
                          {/* Project Duration Info */}
                          {projectData.start_date && projectData.end_date && (
                            <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
                              <div className="flex items-center gap-2 text-sm">
                                <Calendar className="h-4 w-4 text-primary" />
                                <span className="text-primary font-medium">
                                  Project spans {projectDayCount} day{projectDayCount !== 1 ? 's' : ''}
                                </span>
                              </div>
                              {isMultiDayProject && (
                                <p className="text-xs text-muted-foreground mt-1 ml-6">
                                  After saving, you'll be prompted to duplicate the shift template across all days.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </TabsContent>
                    
                    {/* TAB 2: Shift Details */}
                    <TabsContent value="shift" className="h-full m-0 p-0">
                      <ScrollArea className="h-full">
                        <div className="p-6 space-y-6">
                          {/* Shift Name */}
                          <div className="space-y-2">
                            <Label>Shift Name *</Label>
                            <Input
                              value={shiftData.name}
                              onChange={(e) => setShiftData(prev => ({ ...prev, name: e.target.value }))}
                              placeholder="Enter shift name"
                              className={cn(errors.shift_name && "border-destructive")}
                            />
                            {errors.shift_name && <p className="text-xs text-destructive">{errors.shift_name}</p>}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            {/* Venue */}
                            <div className="space-y-2">
                              <Label className="flex items-center gap-1">
                                <MapPin className="h-3.5 w-3.5" />
                                Venue *
                              </Label>
                              <Select value={shiftData.venue_id} onValueChange={handleVenueChange}>
                                <SelectTrigger className={cn(errors.venue_id && "border-destructive")}>
                                  <SelectValue placeholder="Select venue" />
                                </SelectTrigger>
                                <SelectContent>
                                  {venues.map((venue) => (
                                    <SelectItem key={venue.id} value={venue.id}>
                                      {venue.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {!shiftData.venue_id && shiftData.venue_name && (
                                <UnmatchedWarning message={`No match for: ${shiftData.venue_name}`} />
                              )}
                              {errors.venue_id && <p className="text-xs text-destructive">{errors.venue_id}</p>}
                            </div>
                            
                            {/* Location */}
                            <div className="space-y-2">
                              <Label className="flex items-center gap-1">
                                <MapPin className="h-3.5 w-3.5" />
                                Location
                              </Label>
                              <Select
                                value={shiftData.venue_location_id}
                                onValueChange={handleLocationChange}
                                disabled={!shiftData.venue_id}
                              >
                                <SelectTrigger className={cn(!shiftData.venue_id && "opacity-50")}>
                                  <SelectValue placeholder={shiftData.venue_id ? "Select location" : "Select venue first"} />
                                </SelectTrigger>
                                <SelectContent>
                                  {filteredLocations.map((location) => (
                                    <SelectItem key={location.id} value={location.id}>
                                      {location.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {!shiftData.venue_location_id && shiftData.venue_location_name && (
                                <UnmatchedWarning message={`No match for: ${shiftData.venue_location_name}`} />
                              )}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-4 gap-4">
                            {/* Date */}
                            <div className="space-y-2">
                              <Label className="flex items-center gap-1">
                                <Calendar className="h-3.5 w-3.5" />
                                Date *
                              </Label>
                              <Input
                                type="date"
                                value={shiftData.date}
                                onChange={(e) => setShiftData(prev => ({ ...prev, date: e.target.value }))}
                                className={cn(errors.shift_date && "border-destructive")}
                              />
                            </div>
                            
                            {/* Start Time */}
                            <div className="space-y-2">
                              <Label className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                Start *
                              </Label>
                              <Input
                                type="time"
                                value={shiftData.start_time}
                                onChange={(e) => setShiftData(prev => ({ ...prev, start_time: e.target.value }))}
                                className={cn(errors.start_time && "border-destructive")}
                              />
                            </div>
                            
                            {/* Hours */}
                            <div className="space-y-2">
                              <Label className="flex items-center gap-1">
                                <Timer className="h-3.5 w-3.5" />
                                Hours
                              </Label>
                              <Input
                                type="number"
                                min="1"
                                max="24"
                                value={shiftData.duration_hours}
                                onChange={(e) => setShiftData(prev => ({ ...prev, duration_hours: parseFloat(e.target.value) || 8 }))}
                              />
                            </div>
                            
                            {/* Crew */}
                            <div className="space-y-2">
                              <Label className="flex items-center gap-1">
                                <Users className="h-3.5 w-3.5" />
                                Crew *
                              </Label>
                              <Input
                                type="number"
                                min="1"
                                max="100"
                                value={shiftData.staff_required}
                                onChange={(e) => setShiftData(prev => ({ ...prev, staff_required: parseInt(e.target.value) || 1 }))}
                                className={cn(errors.staff_required && "border-destructive")}
                              />
                            </div>
                          </div>
                        </div>
                      </ScrollArea>
                    </TabsContent>
                    
                    {/* TAB 3: Tasks */}
                    <TabsContent value="tasks" className="h-full m-0 p-0">
                      <ScrollArea className="h-full">
                        <div className="p-6 space-y-4">
                          <div className="flex items-center justify-between">
                            <Label className="text-base">Tasks</Label>
                            <Button variant="outline" size="sm" onClick={addTask}>
                              <Plus className="h-4 w-4 mr-1" />
                              Add Task
                            </Button>
                          </div>
                          
                          {taskItems.length === 0 ? (
                            <div className="p-8 text-center bg-surface-elevated rounded-lg border border-dashed">
                              <ClipboardList className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                              <p className="text-sm text-muted-foreground">No tasks detected from email</p>
                              <Button variant="outline" size="sm" className="mt-3" onClick={addTask}>
                                <Plus className="h-4 w-4 mr-1" />
                                Add Task
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {taskItems.map((task, index) => (
                                <div key={task.id} className="flex items-center gap-3 p-3 bg-surface-elevated rounded-lg border">
                                  <div className="flex-1 grid grid-cols-3 gap-3">
                                    <div className="col-span-2">
                                      <Label className="text-xs text-muted-foreground mb-1 block">Task Name</Label>
                                      <Select
                                        value={task.name}
                                        onValueChange={(v) => updateTask(task.id, 'name', v)}
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder="Select or type task" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {task.name && !taskDefinitions.find(t => t.name === task.name) && (
                                            <SelectItem value={task.name}>
                                              {task.name} (custom)
                                            </SelectItem>
                                          )}
                                          {taskDefinitions.map((td) => (
                                            <SelectItem key={td.id} value={td.name}>
                                              {td.name}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div>
                                      <Label className="text-xs text-muted-foreground mb-1 block">Quantity</Label>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={task.quantity}
                                        onChange={(e) => updateTask(task.id, 'quantity', parseInt(e.target.value) || 1)}
                                      />
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-muted-foreground hover:text-destructive"
                                    onClick={() => removeTask(task.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </TabsContent>
                    
                    {/* TAB 4: Crew Requirements */}
                    <TabsContent value="crew" className="h-full m-0 p-0">
                      <ScrollArea className="h-full">
                        <div className="p-6 space-y-6">
                          <div>
                            <Label className="text-base mb-3 block">Crew Requirements</Label>
                            <div className="space-y-3">
                              {crewRequirements.map((req, index) => (
                                <div key={index} className="flex items-center gap-3 p-3 bg-surface-elevated rounded-lg border">
                                  <div className="flex-1 grid grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs text-muted-foreground mb-1 block">Role</Label>
                                      <Input
                                        value={req.role_name}
                                        onChange={(e) => updateCrewRequirement(index, 'role_name', e.target.value)}
                                        placeholder="Role name"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs text-muted-foreground mb-1 block">Quantity</Label>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={req.quantity}
                                        onChange={(e) => updateCrewRequirement(index, 'quantity', parseInt(e.target.value) || 1)}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Rule-managed supplements info */}
                          {(ruleSupplements.length > 0 || estimatedRuleSupplements.length > 0) && (
                            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                              <h4 className="text-sm font-medium mb-2 flex items-center gap-2 text-amber-400">
                                <Sparkles className="h-4 w-4" />
                                Rule-Managed Supplements
                              </h4>
                              <div className="space-y-2">
                                {ruleSupplements.length > 0 && (
                                  <div>
                                    <p className="text-xs text-muted-foreground mb-1">Detected in email:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {ruleSupplements.map((s, i) => (
                                        <Badge key={i} variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400">
                                          {s}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {estimatedRuleSupplements.length > 0 && (
                                  <div>
                                    <p className="text-xs text-muted-foreground mb-1">Estimated from shift config:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {estimatedRuleSupplements.map((s, i) => (
                                        <Badge key={i} variant="outline" className="text-xs bg-green-500/10 border-green-500/30 text-green-400">
                                          {s}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-2">
                                These will be applied automatically by system rules after save.
                              </p>
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </TabsContent>
                    
                    {/* TAB 5: Notes */}
                    <TabsContent value="notes" className="h-full m-0 p-0">
                      <ScrollArea className="h-full">
                        <div className="p-6">
                          <Label className="mb-2 block">Notes</Label>
                          <Textarea
                            value={shiftData.notes}
                            onChange={(e) => setShiftData(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder="Any additional notes for this shift..."
                            rows={8}
                            className="resize-none"
                          />
                          <p className="text-xs text-muted-foreground mt-2">
                            Notes will be saved with each shift created from this template.
                          </p>
                        </div>
                      </ScrollArea>
                    </TabsContent>
                  </div>
                </Tabs>
              </div>
              
              {/* Summary Sidebar */}
              <div className="w-64 border-l bg-surface-elevated p-4 flex-shrink-0 overflow-auto">
                <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
                  <Settings2 className="h-4 w-4" />
                  Summary
                </h3>
                
                <div className="space-y-4">
                  {/* Project Summary */}
                  <Card className="bg-card/50">
                    <CardHeader className="py-2 px-3">
                      <CardTitle className="text-xs text-muted-foreground">PROJECT</CardTitle>
                    </CardHeader>
                    <CardContent className="px-3 pb-3 pt-0">
                      <p className="text-sm font-medium truncate">
                        {projectData.project_name || "Untitled"}
                      </p>
                      {projectData.start_date && projectData.end_date && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {projectData.start_date} → {projectData.end_date}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Shift Stats */}
                  <Card className="bg-card/50">
                    <CardHeader className="py-2 px-3">
                      <CardTitle className="text-xs text-muted-foreground">SHIFT TEMPLATE</CardTitle>
                    </CardHeader>
                    <CardContent className="px-3 pb-3 pt-0 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Shifts</span>
                        <Badge variant="secondary" className="font-medium">
                          {projectDayCount}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Crew/day</span>
                        <Badge variant="secondary" className="font-medium">
                          {shiftData.staff_required}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Hours/day</span>
                        <Badge variant="secondary" className="font-medium">
                          {shiftData.duration_hours}h
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Tasks</span>
                        <Badge variant="secondary" className="font-medium">
                          {taskItems.filter(t => t.name).length}
                        </Badge>
                      </div>
                      <Separator className="my-2" />
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-medium">Total Crew</span>
                        <span className="text-sm font-bold text-primary">
                          {totalCrew}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-medium">Total Hours</span>
                        <span className="text-sm font-bold text-primary">
                          {totalHours}h
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Estimated Supplements */}
                  {estimatedRuleSupplements.length > 0 && (
                    <Card className="bg-green-500/5 border-green-500/20">
                      <CardHeader className="py-2 px-3">
                        <CardTitle className="text-xs text-green-400 flex items-center gap-1">
                          <Sparkles className="h-3 w-3" />
                          ESTIMATED SUPPLEMENTS
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-3 pb-3 pt-0">
                        <div className="flex flex-wrap gap-1">
                          {estimatedRuleSupplements.map((s, i) => (
                            <Badge key={i} variant="outline" className="text-xs bg-green-500/10 border-green-500/30 text-green-400">
                              {s}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="px-6 py-4 border-t flex-shrink-0">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={saving}>
              Cancel Import
            </Button>
            <Button onClick={handleSaveShift} disabled={saving || loading} className="bg-primary hover:bg-primary/90">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Save Shift
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Duplication Modal */}
      <ShiftDuplicationModal
        isOpen={showDuplicationModal}
        onClose={handleSingleShift}
        projectStartDate={projectData.start_date}
        projectEndDate={projectData.end_date}
        excludeDate={shiftData.date}
        onConfirm={handleDuplicationConfirm}
        onSingleShift={handleSingleShift}
      />
    </>
  );
}

export default ShiftImportForm;
